import React, {useContext, useState, useMemo, useEffect} from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import dayjs from "dayjs";

// Services & Store
import { PAContext } from "../../../Services/PAContext";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";


// Helpers
import { capitalizeFirstLetter } from "../../Helpers/TextStyling";

// Sub-Components
import NoteCard from './NoteCard';
import NotesEditDialog from './NotesEditDialog';
import {getAllContactsFromDatabase} from "../../Restricted/Contacts/BrowserDatabaseFunctions";


const DateTab = ({ displayDay, displayDate, noteVolume, isCurrent, onSelect }) => (
    <Box
        onClick={onSelect}
        sx={{
            position: 'relative',
            minWidth: '60px',
            height: '50px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            borderRadius: '14px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            bgcolor: isCurrent ? '#fff' : '#f8fafc',
            border: isCurrent ? '1px solid #06b6d4' : '1px solid #e2e8f0',
            boxShadow: isCurrent ? '0 10px 15px -3px rgba(6, 182, 212, 0.1)' : 'none',
            transform: isCurrent ? 'scale(1.05)' : 'scale(1)',
            '&:hover': { bgcolor: '#fff', borderColor: '#06b6d4' }
        }}
    >
        {/* We use standard <span> if Typography is being weird */}
        <Typography sx={{
            fontSize: '11px',
            fontWeight: 800,
            color: isCurrent ? '#06b6d4' : '#64748b',
            lineHeight: 1.2
        }}>
            {displayDay}
        </Typography>

        <Typography sx={{
            fontSize: '9px',
            fontWeight: 600,
            color: isCurrent ? '#1e293b' : '#94a3b8',
            mt: 0.2
        }}>
            {displayDate}
        </Typography>

        {noteVolume > 0 && (
            <Box sx={{
                position: 'absolute',
                top: -6,
                right: -6,
                minWidth: '18px',
                height: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#10b981',
                color: 'white',
                fontSize: '10px',
                fontWeight: 900,
                borderRadius: '6px',
                border: '2px solid #fff',
            }}>
                {noteVolume}
            </Box>
        )}

        {isCurrent && (
            <Box sx={{
                position: 'absolute',
                bottom: 4,
                width: '12px',
                height: '3px',
                bgcolor: '#06b6d4',
                borderRadius: '2px'
            }} />
        )}
    </Box>
);


function NotesList() {

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, sendSocketMessage } = useContext(PAContext);
    const { Account, SampleAttendees, authenticated } = dataArray;

    const [dbContacts, setDbContacts] = useState([]);
    useEffect(() => {
        if (!authenticated) {
            setDbContacts([]);
            return;
        }

        let cancelled = false;

        getAllContactsFromDatabase()
            .then(contacts => {
                if (cancelled) return;
                setDbContacts(Array.isArray(contacts) ? contacts : []);
            })
            .catch(err => {
                console.error('Failed to load Contacts from browser DB:', err);
                if (!cancelled) {
                    setDbContacts([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [authenticated]);

    const [ attendeesReferenceArray, setAttendeesReferenceArray ] = useState( authenticated
        ? dbContacts
        : SampleAttendees);
    useEffect(() => {

        console.log("authenticated, dbContacts, SampleAttendees triggered ...");

        setAttendeesReferenceArray(
            authenticated
                ? dbContacts
                : SampleAttendees
        );
    }, [authenticated, dbContacts, SampleAttendees]);


    // --- STATE ---
    const [viewType, setViewType] = useState(PA_GlobalObject.Planning.time_table?.dates?.length > 0 ? 'dates' : 'days');
    const [selectedDate, setSelectedDate] = useState('all');
    const [selectedDay, setSelectedDay] = useState('all');

    // UI State
    const [anchorMenu, setAnchorMenu] = useState(null);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [activeNote, setActiveNote] = useState(null); // The note currently being edited/added

    // --- LOGIC: FILTERING & SORTING ---
    const filteredNotes = useMemo(() => {
        let notes = [...(PA_GlobalObject?.Planning?.notes || [])];

        // Permission Filter
        notes = notes.filter(n => !n.private || n.attendee_id === Account.id || n.tagged?.includes(Account.id));

        // Date/Day Filter
        if (viewType === 'dates' && selectedDate !== 'all') {
            notes = notes.filter(n => n.date === selectedDate);
        } else if (viewType === 'days' && selectedDay !== 'all') {
            notes = notes.filter(n => n.day === selectedDay);
        }

        // Sort - Ensure fresh sorting after a date change
        return notes.sort((a, b) => {
            if (viewType === 'dates') return dayjs(a.date).diff(dayjs(b.date));
            const getNum = (s) => parseInt(s?.replace(/\D/g, '') || 0);
            return getNum(a.day) - getNum(b.day);
        });
    }, [PA_GlobalObject.Planning.notes, viewType, selectedDate, selectedDay, Account.id]);

    const groupedNotes = useMemo(() => {
        return filteredNotes.reduce((acc, note) => {
            const key = viewType === 'dates' ? note.date : note.day;
            if (!key) return acc; // Skip notes with no assigned date/day
            if (!acc[key]) acc[key] = [];
            acc[key].push(note);
            return acc;
        }, {});
    }, [filteredNotes, viewType]);

    // --- HANDLERS: ACTIONS ---
    const handleSaveNote = (noteData) => {

        const notesToSave = Array.isArray(noteData) ? noteData : [noteData];

        // 1. Prepare the new notes array for the local store
        let updatedNotes = [...(PA_GlobalObject.Planning.notes || [])];

        notesToSave.forEach(note => {
            const idx = updatedNotes.findIndex(n => n.id === note.id);
            if (idx !== -1) {
                updatedNotes[idx] = note; // Update existing local entry
            } else {
                updatedNotes.push(note); // Add new local entry
            }
        });

        // 2. ALWAYS update the local store (The UI reflects changes immediately)
        set_PA_GlobalObject({
            ...PA_GlobalObject,
            Planning: {
                ...PA_GlobalObject.Planning,
                notes: updatedNotes
            }
        });

        // 3. CONDITIONALLY update the server
        if (PA_GlobalObject.Planning.status !== 10) {
            notesToSave.forEach(note => {
                const isExisting = (PA_GlobalObject.Planning.notes || []).some(n => n.id === note.id);
                sendSocketMessage({
                    action: isExisting ? "E_updateEventNote_v2" : "E_addEventNote_v2",
                    event_id: PA_GlobalObject.Planning.id,
                    note: note
                });
            });
        } else {
            console.warn("Event is status 10 (Planning). Local UI updated, but server sync skipped.");
        }

        setIsEditOpen(false);
        setActiveNote(null);
    };

    const handleDelete = () => {
        if (!activeNote) return;
        sendSocketMessage({
            action: "E_deleteEventNote_v2",
            event_id: PA_GlobalObject.Planning.id,
            note_id: activeNote.id
        });
        setIsDeleteOpen(false);
        setActiveNote(null);
    };





    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

            {/* --- NOTES HEADER --- */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 4px 24px -6px rgba(0,0,0,0.06)',
                    borderRadius: '12px 12px 0 0',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    {/* Title Area (Exact Styling with Gradient Icon & Sub-label) */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{
                            background: 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)',
                            p: 0.9,
                            borderRadius: '10px',
                            display: 'flex',
                            boxShadow: '0 4px 10px rgba(6, 182, 212, 0.3)'
                        }}>
                            <i className="icon-notebook" style={{ color: 'white', fontSize: '18px' }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem', letterSpacing: -0.4, whiteSpace: 'nowrap' }}>
                                Notes
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <i className="icon-clock" style={{ color: '#06b6d4', fontSize: '10px' }} />
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 0.1, whiteSpace: 'nowrap' }}>
                                    Event Briefing
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Controls Area */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                        {/* Segmented View Toggle (Dates/Days) - Wired to setViewType */}
                        <Box sx={{
                            display: 'flex',
                            bgcolor: '#f1f5f9',
                            p: 0.5,
                            borderRadius: '10px',
                            border: '1px solid #e2e8f0'
                        }}>
                            {['dates', 'days'].map((type) => (
                                <Box
                                    key={type}
                                    onClick={() => setViewType(type)}
                                    sx={{
                                        px: 1.5, py: 0.3, borderRadius: '7px', cursor: 'pointer',
                                        bgcolor: viewType === type ? '#fff' : 'transparent',
                                        boxShadow: viewType === type ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                        transition: '0.2s',
                                        display: 'flex', alignItems: 'center'
                                    }}
                                >
                                    <Typography sx={{
                                        fontSize: '10px',
                                        fontWeight: 800,
                                        color: viewType === type ? '#06b6d4' : '#64748b'
                                    }}>
                                        {type.toUpperCase()}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {/* Menu Add Button - Wired to anchorMenu */}
                        <Tooltip title="Add Note">
                            <IconButton
                                onClick={(e) => setAnchorMenu(e.currentTarget)}
                                sx={{
                                    bgcolor: '#1e293b',
                                    color: 'white',
                                    borderRadius: '12px',
                                    width: '38px',
                                    height: '38px',
                                    boxShadow: '0 4px 12px rgba(30, 41, 59, 0.25)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        bgcolor: '#334155',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 6px 16px rgba(30, 41, 59, 0.3)',
                                    }
                                }}
                            >
                                <i className="icon-plus" style={{ fontSize: '20px' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Paper>

            {/* --- TIMETABLE NAVIGATION --- */}
            <Box
                className="notes-list-timetable-list-container"
                sx={{
                    display: 'flex',            // Use flexbox
                    flexDirection: 'row',       // Align children in a row
                    flexWrap: 'nowrap',         // CRITICAL: Prevents chips from jumping to the next line
                    overflow: 'auto',
                    gap: 1.5,                   // Consistent spacing between your new DateTabs
                    py: 2,                      // Padding for the "hover lift" and badges
                    px: 1,
                }}
            >
                {/* "All" Toggle */}
                <Box
                    onClick={() => { setSelectedDate('all'); setSelectedDay('all'); }}
                    sx={{
                        minWidth: '50px',
                        height: '50px', // Matched height with DateTabs
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: '14px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        bgcolor: (selectedDate === 'all' && selectedDay === 'all') ? '#1e293b' : '#f8fafc',
                        color: (selectedDate === 'all' && selectedDay === 'all') ? '#fff' : '#64748b',
                        border: (selectedDate === 'all' && selectedDay === 'all') ? '1px solid #1e293b' : '1px solid #e2e8f0',
                        boxShadow: (selectedDate === 'all' && selectedDay === 'all') ? '0 10px 15px -3px rgba(30, 41, 59, 0.2)' : 'none',
                        '&:hover': { transform: 'translateY(-2px)', borderColor: '#cbd5e1' }
                    }}
                >
                    <Typography sx={{ fontSize: '12px', fontWeight: 900 }}>ALL</Typography>
                </Box>

                {/* Dynamic Date/Day Tabs */}
                {viewType === 'dates' ? (
                    // 1. DATES VIEW
                    (PA_GlobalObject?.Planning?.time_table?.dates || []).map((eventDateObj, idx) => {
                        const eventDate = eventDateObj.date;
                        // Calculate count from the notes array
                        const noteCount = (PA_GlobalObject.Planning.notes || []).filter(n => n.date === eventDate).length;
                        const isSelected = selectedDate === eventDate;

                        return (
                            <DateTab
                                key={`date-${eventDate || idx}`}
                                displayDay={dayjs(eventDate).format('ddd').toUpperCase()}
                                displayDate={dayjs(eventDate).format('M/D')}
                                noteVolume={noteCount}
                                isCurrent={selectedDate === eventDate}
                                onSelect={() => {
                                    setSelectedDate(eventDate);
                                    setSelectedDay('all');
                                }}
                            />
                        );
                    })
                ) : (
                    // 2. DAYS VIEW
                    (PA_GlobalObject?.Planning?.time_table?.days || []).map((dayObj, idx) => {
                        // 1. Identify the key (e.g., "day 1")
                        const dayKey = Object.keys(dayObj).find(k => k.toLowerCase().includes('day')) || `day ${idx + 1}`;

                        // 2. Identify the value (e.g., "Monday")
                        const dayName = dayObj[dayKey];

                        // 3. Logic for count and selection
                        const noteCount = (PA_GlobalObject.Planning.notes || []).filter(n => n.day === dayKey).length;
                        const isSelected = selectedDay === dayKey;

                        return (
                            <DateTab
                                key={`day-${dayKey || idx}`}
                                displayDay={dayName?.slice(0, 3).toUpperCase() || 'DAY'} // e.g., "MON"
                                displayDate={capitalizeFirstLetter(dayKey)}              // e.g., "Day 1"
                                noteVolume={noteCount}
                                isCurrent={isSelected}
                                onSelect={() => {
                                    setSelectedDay(dayKey);
                                    setSelectedDate('all'); // Crucial: clear date selection
                                }}
                            />
                        );
                    })
                )}
            </Box>

            {/* --- 3. THE LIST --- */}
            <Box sx={{ px: 2, pb: 4, flexGrow: 1, overflowY: 'auto' }}>
                {Object.entries(groupedNotes).map(([groupKey, notes]) => (
                    <Box key={groupKey} sx={{ mb: 4 }}>
                        <Typography variant="overline" sx={{ fontWeight: 800, color: '#94a3b8', mb: 1, display: 'block' }}>
                            {viewType === 'dates' ? dayjs(groupKey).format('dddd, MMM D') : capitalizeFirstLetter(groupKey)}
                        </Typography>
                        {notes.map(note => (
                            <NoteCard
                                key={note.id}
                                note={note}
                                attendees={attendeesReferenceArray}
                                onEdit={(n) => { setActiveNote(n); setIsEditOpen(true); }}
                                onDelete={(n) => { setActiveNote(n); setIsDeleteOpen(true); }}
                            />
                        ))}
                    </Box>
                ))}
            </Box>

            {/* --- 4. DIALOGS --- */}
            <NotesEditDialog
                open={isEditOpen}
                note={activeNote}
                onClose={() => { setIsEditOpen(false); setActiveNote(null); }}
                onSave={handleSaveNote}
                viewType={viewType}
                timeTable={PA_GlobalObject.Planning.time_table}
                attendees={PA_GlobalObject.Planning.attendees}
                currentUserId={Account.id}
            />

            <Dialog open={isDeleteOpen} onClose={() => setIsDeleteOpen(false)}>
                <DialogTitle sx={{ fontWeight: 800 }}>Confirm Deletion</DialogTitle>
                <DialogContent>Are you sure you want to delete this note?</DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setIsDeleteOpen(false)}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
                </DialogActions>
            </Dialog>

            <Menu anchorEl={anchorMenu} open={Boolean(anchorMenu)} onClose={() => setAnchorMenu(null)}>
                <MenuItem onClick={() => { setIsEditOpen(true); setActiveNote(null); setAnchorMenu(null); }}>
                    <ListItemIcon><i className="icon-pencil" /></ListItemIcon>
                    <ListItemText primary="Create New Note" />
                </MenuItem>
            </Menu>
        </Box>
    );
}

export default NotesList;